import { useAppState } from 'AppProvider';
import { type ICartExperience } from 'types/cart';
import type { DiscountCode } from 'types/discount-code';
import { ITax } from 'types/experience.types';
import { Listing } from 'types/listings';
import {
  emptyCartTotalDetails,
  computeCartTotalWithCompoundTaxes,
  type CartTotalDetails,
  type ICartExperience as CartPricesCartExperience,
} from '@kouto/cart-prices';
import { CreditCodes } from 'types/credit-codes';
import { GetListingV3PublicResponse } from '@kouto/types';
import { useTranslation } from 'react-i18next';
import useServerCartQuote from 'hooks/useServerCartQuote';
import {
  adaptListingsV3ForCart,
  adaptCartItemsForCart,
} from 'utils/data/taxes/taxes';
import isV3Listings from 'utils/listingsHelpers/isV3Listings';
import { mergeServerQuoteIntoCartTotals } from 'utils/cart/mergeServerQuote';

export type ITaxWithAmount = ITax & { taxAmount: number };

const partitionCartItems = (
  cartItems: Record<string, ICartExperience>,
  quotedCartItemIds: string[],
) =>
  Object.values(cartItems).reduce<{
    quoted: Record<string, ICartExperience>;
    local: Record<string, ICartExperience>;
  }>(
    (partition, item) =>
      quotedCartItemIds.includes(item.cartItemId)
        ? {
            ...partition,
            quoted: { ...partition.quoted, [item.cartItemId]: item },
          }
        : {
            ...partition,
            local: { ...partition.local, [item.cartItemId]: item },
          },
    { quoted: {}, local: {} },
  );

/**
 * What the cart costs.
 *
 * Session-based items are priced here with `@kouto/cart-prices`, as they always were. Continuous items
 * — a collection booked by the hour, the day or the night — are priced by the server's cart-price
 * endpoint and merged in, because their price depends on rate ladders and per-date taxes the plugin
 * does not hold. While that quote is in flight the quoted portion counts as 0 rather than blocking.
 *
 * The coupon is applied to the locally priced items here and to the quoted items by the quote, which
 * carries it. An amount coupon that reaches a continuous item is quoted with every item it reaches, so
 * it is spent once across the cart, as the charge spends it. Credit codes are applied to the locally
 * priced total only, so the guest pays no more than shown.
 */
export default function useTotalCartAmount(
  cartItems: Record<string, ICartExperience> | false,
  listings:
    | Record<string, Listing>
    | Record<string, GetListingV3PublicResponse>,
): CartTotalDetails & { isQuotePending: boolean } {
  const { t } = useTranslation();

  const { discountCode }: DiscountCode = useAppState(
    (state: Record<string, unknown>) => state.discountCode,
  );

  const { creditCodes }: CreditCodes = useAppState(
    (state: Record<string, unknown>) => state.creditCodes,
  );

  const { quotedCartItemIds, isQuotePending, quote } = useServerCartQuote(
    cartItems,
    listings,
  );

  if (!cartItems) return { ...emptyCartTotalDetails, isQuotePending: false };

  const { quoted, local } = partitionCartItems(cartItems, quotedCartItemIds);

  // Convert V3 listings to cart-compatible format if needed
  const compatibleListings = isV3Listings(listings)
    ? adaptListingsV3ForCart(listings)
    : listings;

  // Convert cart items' addOns taxes to cart-compatible format
  const compatibleCartItems = adaptCartItemsForCart(local) as unknown as Record<
    string,
    CartPricesCartExperience
  >;

  const cartDetails = computeCartTotalWithCompoundTaxes(
    compatibleCartItems,
    compatibleListings,
    {
      discountCode,
      creditCodes,
    },
  );

  if (quotedCartItemIds.length === 0) {
    return { ...cartDetails, isQuotePending: false };
  }

  return {
    ...mergeServerQuoteIntoCartTotals(cartDetails, quote, quoted, listings, {
      fallbackTaxName: t('taxesAndFees'),
    }),
    isQuotePending,
  };
}
